"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CarouselArrow from "@/components/ui/CarouselArrow";
import {
  type Product,
  type ProductImageSource,
  getProductImageSources,
  getProductName,
} from "@/lib/product-utils";

import ProductImage from "./ProductImage";

interface ImageCarouselProps {
  // Either provide images array directly OR a product object
  images?: Array<string | ProductImageSource>;
  product?: Product | null;
  productName?: string;
  objectFit: string;
  onImageLoad?: (img: { naturalWidth: number; naturalHeight: number }) => void;
  onImageError?: () => void;
  onImageChange?: (index: number) => void;
  className?: string;
  /** When true, only the first image is eagerly loaded with priority */
  priorityFirst?: boolean;
  /** When true, automatically show the next distinct image while hovered */
  autoHoverSwap?: boolean;
}

/**
 * Image carousel component with touch/swipe support and progressive loading.
 * Images are loaded once and kept mounted to prevent refetching.
 */
export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images: imagesProp,
  product,
  productName: productNameProp,
  objectFit,
  onImageLoad,
  onImageError,
  onImageChange,
  className = "",
  priorityFirst = false,
  autoHoverSwap = false,
}) => {
  // Determine images and product name from props
  const imageSources = useMemo<ProductImageSource[]>(() => {
    if (imagesProp && imagesProp.length > 0) {
      return imagesProp
        .map((image) => {
          if (typeof image === "string") {
            return { primary: image } as ProductImageSource;
          }
          return image;
        })
        .filter((image): image is ProductImageSource => Boolean(image?.primary));
    }

    if (product) {
      return getProductImageSources(product);
    }

    return [];
  }, [imagesProp, product]);

  const productName = useMemo(() => {
    if (productNameProp) return productNameProp;
    if (product) return getProductName(product);
    return "Product";
  }, [productNameProp, product]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  // Start with only the first image loaded to reduce initial load
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const touchStartXRef = useRef<number | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const hoverPreviousIndexRef = useRef<number>(0);
  const hoverActiveRef = useRef(false);
  const isHoveringRef = useRef(false); // Track actual hover state
  const selectedImageIndexRef = useRef(selectedImageIndex);
  const onImageErrorRef = useRef(onImageError);
  const failedFrameIndicesRef = useRef<Set<number>>(new Set());
  const reportedFrameIndicesRef = useRef<Set<number>>(new Set());

  selectedImageIndexRef.current = selectedImageIndex;
  onImageErrorRef.current = onImageError;

  useEffect(() => {
    failedFrameIndicesRef.current = new Set();
    reportedFrameIndicesRef.current = new Set();
  }, [imageSources]);

  // Preload adjacent images for smooth transitions
  useEffect(() => {
    if (imageSources.length <= 1) return;

    const nextIndex = (selectedImageIndex + 1) % imageSources.length;
    const prevIndex = selectedImageIndex === 0 ? imageSources.length - 1 : selectedImageIndex - 1;

    // Also preload the next distinct image for potential hover swap
    let nextDistinctIndex: number | null = null;
    if (autoHoverSwap) {
      const current = imageSources[selectedImageIndex]?.primary;
      for (let offset = 1; offset < imageSources.length; offset += 1) {
        const candidateIndex = (selectedImageIndex + offset) % imageSources.length;
        const candidate = imageSources[candidateIndex]?.primary;
        if (candidate && candidate !== current) {
          nextDistinctIndex = candidateIndex;
          break;
        }
      }
    }

    setLoadedImages((prev) => {
      const newSet = new Set([...prev, nextIndex, prevIndex]);
      if (nextDistinctIndex !== null) {
        newSet.add(nextDistinctIndex);
      }
      return newSet;
    });
  }, [selectedImageIndex, imageSources, autoHoverSwap]);

  const changeImage = useCallback(
    (newIndex: number, options: { isHoverTransition?: boolean } = {}) => {
      if (newIndex === selectedImageIndex) return;

      // Add the new image index to loaded images
      setLoadedImages((prev) => new Set([...prev, newIndex]));
      setSelectedImageIndex(newIndex);

      if (hoverActiveRef.current && !options.isHoverTransition) {
        hoverPreviousIndexRef.current = newIndex;
      }

      // Notify parent of image change
      onImageChange?.(newIndex);
    },
    [selectedImageIndex, onImageChange]
  );

  const findNextDistinctImageIndex = useCallback((): number | null => {
    if (imageSources.length <= 1) return null;
    const current = imageSources[selectedImageIndex]?.primary;
    for (let offset = 1; offset < imageSources.length; offset += 1) {
      const candidateIndex = (selectedImageIndex + offset) % imageSources.length;
      const candidate = imageSources[candidateIndex]?.primary;
      if (candidate && candidate !== current) {
        return candidateIndex;
      }
    }
    return null;
  }, [imageSources, selectedImageIndex]);

  const handleFrameError = useCallback((index: number) => {
    failedFrameIndicesRef.current.add(index);

    // Hidden preloaded frames can fail independently; if the current
    // selection fails, surface it immediately. Otherwise remember the
    // failure so we can surface it if the user later navigates there.
    if (index !== selectedImageIndexRef.current) {
      return;
    }

    if (reportedFrameIndicesRef.current.has(index)) {
      return;
    }

    reportedFrameIndicesRef.current.add(index);
    onImageErrorRef.current?.();
  }, []);

  useEffect(() => {
    if (!failedFrameIndicesRef.current.has(selectedImageIndex)) {
      return;
    }

    if (reportedFrameIndicesRef.current.has(selectedImageIndex)) {
      return;
    }

    reportedFrameIndicesRef.current.add(selectedImageIndex);
    onImageErrorRef.current?.();
  }, [selectedImageIndex]);

  // Touch/swipe handling for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    if (!hasInteracted) setHasInteracted(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(deltaX) > 40 && imageSources.length > 1) {
      if (deltaX < 0) {
        // swipe left -> next
        changeImage((selectedImageIndex + 1) % imageSources.length);
      } else {
        // swipe right -> prev
        changeImage(selectedImageIndex === 0 ? imageSources.length - 1 : selectedImageIndex - 1);
      }
    }
    touchStartXRef.current = null;
  };

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasInteracted) setHasInteracted(true);
    changeImage(selectedImageIndex === 0 ? imageSources.length - 1 : selectedImageIndex - 1);
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasInteracted) setHasInteracted(true);
    changeImage((selectedImageIndex + 1) % imageSources.length);
  };

  const handleMouseEnter = () => {
    if (!hasInteracted) setHasInteracted(true);
    isHoveringRef.current = true;

    if (!autoHoverSwap) {
      return;
    }
    if (hoverActiveRef.current) {
      return;
    }

    const nextIndex = findNextDistinctImageIndex();
    if (nextIndex === null) {
      return;
    }

    // Pre-load the image if not already loaded before showing it
    if (!loadedImages.has(nextIndex)) {
      setLoadedImages((prev) => new Set([...prev, nextIndex]));
      // Small delay to allow image to start loading
      setTimeout(() => {
        // Only proceed if user is still hovering and swap hasn't been activated
        if (isHoveringRef.current && !hoverActiveRef.current) {
          hoverPreviousIndexRef.current = selectedImageIndex;
          hoverActiveRef.current = true;
          changeImage(nextIndex, { isHoverTransition: true });
        }
      }, 10);
    } else {
      hoverPreviousIndexRef.current = selectedImageIndex;
      hoverActiveRef.current = true;
      changeImage(nextIndex, { isHoverTransition: true });
    }
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;

    if (!autoHoverSwap) {
      return;
    }

    if (!hoverActiveRef.current) {
      return;
    }

    const previousIndex = hoverPreviousIndexRef.current ?? 0;
    hoverActiveRef.current = false;
    changeImage(previousIndex, { isHoverTransition: true });
  };

  // Handle case where no images are available
  if (imageSources.length === 0) {
    return (
      <div className={className}>
        <ProductImage
          product={product}
          alt={productName}
          objectFit={objectFit}
          onLoad={onImageLoad}
          onError={onImageError}
          priority
          loading="eager"
        />
      </div>
    );
  }

  return (
    <div
      className={`${className} group touch-pan-y`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Render all loaded images, but only show the selected one */}
      {imageSources.map((imageSource, index) => {
        // Only render images that have been loaded
        if (!loadedImages.has(index)) return null;

        return (
          <div
            key={`image-${index}`}
            className="absolute inset-0 transition-opacity duration-300 ease-in-out"
            style={{
              opacity: selectedImageIndex === index ? 1 : 0,
              zIndex: selectedImageIndex === index ? 2 : 1,
              pointerEvents: selectedImageIndex === index ? "auto" : "none",
            }}
          >
            <ProductImage
              src={imageSource.primary}
              fallbackSrc={imageSource.fallback}
              alt={`${productName} - Image ${index + 1}`}
              objectFit={objectFit}
              onLoad={onImageLoad}
              onError={() => handleFrameError(index)}
              priority={priorityFirst && index === 0}
              loading={priorityFirst && index === 0 ? "eager" : "lazy"}
            />
          </div>
        );
      })}

      {/* Navigation arrows - only show if multiple images */}
      {imageSources.length > 1 && (
        <>
          <CarouselArrow
            direction="prev"
            onClick={handlePrevClick}
            className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 opacity-0 group-hover:opacity-100 sm:flex"
          />
          <CarouselArrow
            direction="next"
            onClick={handleNextClick}
            className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 opacity-0 group-hover:opacity-100 sm:flex"
          />
        </>
      )}
    </div>
  );
};

export default ImageCarousel;
